
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import List from '@/common/components/UI/List/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// module components
import RoutesListItem from '@/modules/GatewayModule/components/RoutesListItem/index.vue'
import RoutesActions from '@/modules/GatewayModule/components/RoutesActions/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { GatewayMicroserviceT, GatewayRouteT } from '@/core/types/gateway.types'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'

// composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        list: List,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'routes-list-item': RoutesListItem,
        'routes-actions': RoutesActions,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            routes: new DataState<GatewayRouteT>({}),
            microservices: new DataState<GatewayMicroserviceT>({}),
        })

        const filters_state = reactive({
            service_id: null as number | null,
            service: null as string | null,
            method: null as string | null,
            path: null as string | null,
        })

        const notifications = useNotifications()

        const loadRoutes = async (page: number) => {
            state.routes.pagination.current_page = page

            try {
                state.routes.is_loading = true
                const params = {
                    serviceId: filters_state.service_id?.toString() || undefined,
                    service: filters_state.service || undefined,
                    path: filters_state.path || undefined,
                    method: filters_state.method || undefined,
                    limit: 100,
                }

                const response = await gateway.getRoutes(page, params)
                state.routes.list = response.data
                state.routes.pagination.total_pages = response.meta.last_page

                state.routes.is_failed = false
                state.routes.failure.error_state.is_cancel = false
            } catch (error) {
                state.routes.failure.catchError(error)

                if (!state.routes.failure.error_state.is_cancel) {
                    state.routes.is_failed = true
                }
            } finally {
                if (!state.routes.failure.error_state.is_cancel) {
                    state.routes.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadRoutes(1)
            loadMicroservices(1)
        })

        const { actions_state, startCreate, startUpdate, startDelete, startBind } =
            useActions<GatewayRouteT>()

        const createRoute = async (route: GatewayRouteT) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.createRoute(route)
                await loadRoutes(state.routes.pagination.current_page)
                actions_state.is_actions_opened = false

                notifications.notify({
                    title: 'Роут успешно создан!',
                    type: 'success',
                })
            } catch (error) {
                console.log(error)
                notifications.notifyError({ error, title: 'При создании роута произошла ошбика' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateRoute = async (route: GatewayRouteT) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.updateRoute(route)
                await loadRoutes(state.routes.pagination.current_page)
                actions_state.is_actions_opened = false

                notifications.notify({
                    title: 'Роут успешно отредактирован!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При редактировании роута произошла ошбика',
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const bindRoute = async (scopeId: number, routeId: number) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.linkRoute(scopeId, routeId)
                await loadRoutes(state.routes.pagination.current_page)
                actions_state.is_actions_opened = false

                notifications.notify({
                    title: 'Роут успешно закреплен!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При закреплении роута произошла ошбика',
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteRoute = async (route: GatewayRouteT) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.deleteRoute(route.id)
                await loadRoutes(state.routes.pagination.current_page)
                actions_state.is_actions_opened = false

                notifications.notify({
                    title: 'Роут успешно удален!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При удалении роута произошла ошбика',
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const resetFilters = () => {
            filters_state.service = null
            filters_state.service_id = null
            filters_state.method = null
            filters_state.path = null

            loadRoutes(1)
        }

        const searchRoute = async () => {
            state.is_loading = true

            await loadRoutes(1)

            state.is_loading = false
        }

        //

        const loadMicroservices = async (page: number) => {
            state.microservices.pagination.current_page = page

            try {
                state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page)
                state.microservices.list = response.data
                state.microservices.pagination.total_pages = response.meta.last_page

                state.microservices.is_failed = false
                state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                state.microservices.failure.catchError(error)

                if (!state.microservices.failure.error_state.is_cancel) {
                    state.microservices.is_failed = true
                }
            } finally {
                if (!state.microservices.failure.error_state.is_cancel) {
                    state.microservices.is_loading = false
                }
            }
        }

        const loadMoreMicroservices = async (page: number) => {
            if (page > state.microservices.pagination.total_pages) return
            state.microservices.pagination.current_page = page

            try {
                state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page)
                state.microservices.list = state.microservices.list.concat(response.data)
                state.microservices.pagination.total_pages = response.meta.last_page

                state.microservices.is_failed = false
                state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                state.microservices.failure.catchError(error)

                if (!state.microservices.failure.error_state.is_cancel) {
                    state.microservices.is_failed = true
                }
            } finally {
                if (!state.microservices.failure.error_state.is_cancel) {
                    state.microservices.is_loading = false
                }
            }
        }

        //

        return {
            state,
            filters_state,
            loadRoutes,
            actions_state,
            startCreate,
            startUpdate,
            startDelete,
            startBind,
            createRoute,
            updateRoute,
            bindRoute,
            deleteRoute,
            resetFilters,
            searchRoute,
            loadMicroservices,
            loadMoreMicroservices,
        }
    },
})
